import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import PatientPortalShell from '@/modules/referral/components/PatientPortalShell';
import { patientPortalApi } from '../api/patientPortal.api';
import CareVisitCard from '../components/CareVisitCard';
import { IITH, SERIF } from '@/pages/landing/iithBrand';

export default function PatientCareHistoryPage() {
  const [params] = useSearchParams();
  const patientId = params.get('patient_id') || '';
  const [family, setFamily] = useState([]);
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(patientId);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      patientPortalApi.family(),
      patientPortalApi.careHistory(activeId || undefined),
    ])
      .then(([fam, care]) => {
        setFamily(fam?.members || []);
        if (!activeId && fam?.self_id) setActiveId(fam.self_id);
        setVisits(care?.visits || []);
      })
      .catch(() => { setFamily([]); setVisits([]); })
      .finally(() => setLoading(false));
  }, [activeId]);

  const memberName = useMemo(
    () => family.find((m) => m.id === activeId)?.name || 'Patient',
    [family, activeId],
  );

  return (
    <PatientPortalShell>
      <h1 className="text-2xl font-bold text-[#071B34]" style={{ fontFamily: SERIF }}>Care history</h1>
      <p className="text-sm text-[#5A6B7C] mb-4">
        Full medical record for {memberName} — expand any visit for vitals, notes and detailed prescriptions.
      </p>

      {family.length > 1 ? (
        <div className="mb-5 flex flex-wrap gap-2">
          {family.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => setActiveId(m.id)}
              className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${
                activeId === m.id ? 'text-white border-transparent' : 'border-[#D5DEE8] text-[#5A6B7C] bg-white'
              }`}
              style={activeId === m.id ? { background: IITH.saffron } : undefined}
            >
              {m.name}
            </button>
          ))}
        </div>
      ) : null}

      {loading ? (
        <div className="py-16 text-center"><Loader2 className="h-7 w-7 animate-spin mx-auto text-[#F15A22]" /></div>
      ) : visits.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-[#D5DEE8] py-12 text-center text-sm text-[#7A8B9C]">
          No consultation records yet for this person.
        </p>
      ) : (
        <ol className="space-y-3" id="prescriptions" data-testid="patient-care-list">
          {visits.map((v) => <CareVisitCard key={v.id} visit={v} />)}
        </ol>
      )}
    </PatientPortalShell>
  );
}
